import React from 'react';
import { Accordion, Badge, Code, Group, Popover, Stack, Text } from '@mantine/core';
import {
  DEFAULT_TRACE_LABELS,
  TraceLabels,
  TruncatedEnvelope,
  formatBytes,
  isTruncated
} from './traceShapes';
import classes from './TraceValueView.module.css';

/**
 * Format a generic value as a short inline display string.
 */
const formatShortValue = ( v: unknown, maxLen: number ): { display: string; truncated: boolean } => {
  if ( v === null || v === undefined ) {
    return { display: String( v ), truncated: false };
  }
  if ( typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ) {
    const s = String( v );
    return { display: s.length > maxLen ? `${s.substring( 0, maxLen )}…` : s, truncated: s.length > maxLen };
  }
  const json = JSON.stringify( v );
  return {
    display: json.length > maxLen ? `${json.substring( 0, maxLen )}…` : json,
    truncated: json.length > maxLen
  };
};

type TruncatedCardProps = {
  value: TruncatedEnvelope;
  labels: TraceLabels;
};

const TruncatedCard: React.FC<TruncatedCardProps> = ({ value, labels }) => {
  const isCircular = value.circular === true;
  const title = isCircular
    ? `Circular · ${value.type}`
    : `${value.type} · ${formatBytes( value.size )}`;

  return (
    <Accordion variant="filled" radius="xs" className={classes.truncatedAccordion}>
      <Accordion.Item value="truncated">
        <Accordion.Control>
          <Group gap="xs" wrap="nowrap">
            {isCircular && <Badge color="red" size="xs" variant="light">circular</Badge>}
            <Text size="xs" fw={600}>{title}</Text>
            {value.length !== undefined && (
              <Badge size="xs" variant="light" color="gray">len {value.length}</Badge>
            )}
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap={6}>
            {value.preview && (
              <Code block className={classes.truncatedPreview}>
                {value.preview}
              </Code>
            )}
            {value.keys && value.keys.length > 0 && (
              <Group gap={4}>
                {value.keys.map(( k ) => (
                  <Badge key={k} size="xs" variant="outline" color="gray">{k}</Badge>
                ))}
              </Group>
            )}
            <Text size="10px" c="dimmed" fs="italic">
              {labels.fullValueNotStored}
            </Text>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export type TraceValueViewProps = {
  value: unknown;
  /** Max chars for inline fallback before popover. */
  maxInlineLen?: number;
  /** Render compact (single-line) mode — e.g. for edge labels. */
  compact?: boolean;
  /** Translated UI labels. Defaults to hardcoded Spanish when omitted. */
  traceLabels?: TraceLabels;
};

/**
 * Renders a serialized trace value, dispatching on its shape.
 * Falls back to the existing inline/popover JSON rendering.
 */
export const TraceValueView: React.FC<TraceValueViewProps> = ({
  value,
  maxInlineLen = 80,
  compact = false,
  traceLabels
}) => {
  const [ fallbackOpen, setFallbackOpen ] = React.useState( false );
  const labels = traceLabels ?? DEFAULT_TRACE_LABELS;

  if ( isTruncated( value )) {
    if ( compact ) {
      return (
        <Badge size="xs" variant="light" color="orange">
          {value.type} · {formatBytes( value.size )}
        </Badge>
      );
    }
    return <TruncatedCard value={value} labels={labels} />;
  }

  // Fallback: primitives inline, complex via popover.
  const { display, truncated } = formatShortValue( value, maxInlineLen );
  if ( !truncated ) {
    return <span className={classes.fallbackValue}>{display}</span>;
  }
  return (
    <Popover opened={fallbackOpen} onChange={setFallbackOpen} width={360} position="bottom" withArrow shadow="md">
      <Popover.Target>
        <button
          type="button"
          className={classes.fallbackExpandBtn}
          onClick={() => setFallbackOpen(( o ) => !o )}
        >
          <span className={classes.fallbackValue}>{display}</span>
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <Code block style={{ maxHeight: 300, overflow: 'auto', fontSize: 11 }}>
          {JSON.stringify( value, null, 2 )}
        </Code>
      </Popover.Dropdown>
    </Popover>
  );
};
